import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, Users } from 'lucide-react';
import { customerAPI } from '../../services/api';

/**
 * Reusable searchable customer picker with server-side search.
 * 
 * Props:
 * - value: string (customer name or search text)
 * - customerId: string (selected customer ID)
 * - onSelect: (customer) => void — called when customer is selected
 * - onClear: () => void — called when cleared
 * - placeholder: string
 * - disabled: boolean
 * - className: string
 * - testId: string (data-testid)
 */
const CustomerSearchInput = ({
  value = '',
  customerId = '',
  onSelect,
  onClear,
  placeholder = 'Search customer by name, GSTIN, or email...',
  disabled = false,
  className = '',
  testId = 'customer-search',
}) => {
  const [search, setSearch] = useState(value);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(!!customerId);
  const containerRef = useRef();
  const debounceRef = useRef();

  useEffect(() => {
    setSearch(value);
    setSelected(!!customerId);
  }, [value, customerId]);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const doSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    try {
      const data = await customerAPI.getAll({ search: query, page: 1, page_size: 15 });
      const items = Array.isArray(data) ? data : (data?.items || data || []);
      setResults(items);
      setShowDropdown(items.length > 0);
    } catch {
      setResults([]);
    }
    setLoading(false);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setSelected(false);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (customer) => {
    setSearch(customer.name);
    setSelected(true);
    setShowDropdown(false);
    setResults([]);
    onSelect?.(customer);
  };

  const handleClear = () => {
    setSearch('');
    setSelected(false);
    setResults([]);
    setShowDropdown(false);
    onClear?.();
  };

  const handleFocus = () => {
    if (!selected && search.length < 2) {
      // Load initial results on focus
      doSearch(search || 'a');
    } else if (results.length > 0 && !selected) {
      setShowDropdown(true);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          value={search}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={(e) => e.key === 'Escape' && setShowDropdown(false)}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          className={`w-full pl-8 pr-8 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 ${
            selected ? 'bg-blue-50/50 border-blue-200' : ''
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${className}`}
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />
        )}
        {!loading && search && !disabled && (
          <button
            onClick={handleClear}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
            tabIndex={-1}
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-[250px] overflow-y-auto">
          {results.map((customer) => (
            <div
              key={customer.id}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(customer); }}
              data-testid={`customer-option-${customer.id}`}
              className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 cursor-pointer"
            >
              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-3 h-3 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-900 truncate">{customer.name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {customer.gstin && (
                    <span className="text-[9px] font-mono text-gray-500">GSTIN: {customer.gstin}</span>
                  )}
                  {customer.city && (
                    <span className="text-[9px] text-gray-400">{customer.city}{customer.state ? `, ${customer.state}` : ''}</span>
                  )}
                  {customer.phone && (
                    <span className="text-[9px] text-gray-400">{customer.phone}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && results.length === 0 && search.length >= 2 && !loading && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center">
          <p className="text-[10px] text-gray-500">No customers found for "{search}"</p>
        </div>
      )}
    </div>
  );
};

export default CustomerSearchInput;
